// Files that travel with an ai_jobs row. Uploaded to R2 through r2-job-upload,
// recorded on brief.attachments, and linked from the queue note so the agent
// reading the job gets the same public URLs a human sees in the panel.

import { supabase } from './supabase';
import { cancelAiJob, enqueueAiJob, type EnqueueAiJobInput } from './aiJobs';
import type { AiJob, AiJobAttachment } from './supabase.types';

export type { AiJobAttachment };

/** Matches r2-job-upload. */
export const MAX_ATTACHMENT_SIZE = 25 * 1024 * 1024; // 25 MB
export const MAX_ATTACHMENTS = 10;

/** Picker filter only — r2-job-upload is the real gate. */
export const ATTACHMENT_ACCEPT =
  '.pdf,.doc,.docx,.txt,.md,.markdown,.rtf,.html,.htm,.csv,.tsv,.json,.xml,.yaml,.yml,.xls,.xlsx,.ppt,.pptx,.zip,.png,.jpg,.jpeg,.gif,.webp,.svg,.heic';
export const ATTACHMENT_HINT = 'PDF, DOC, MD, TXT, CSV, XLS, PPT, ZIP, images · up to 25 MB each';

export function readAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error || new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/** Attachments recorded on a job of any kind; empty when the brief has none. */
export function jobAttachments(job: Pick<AiJob, 'brief'>): AiJobAttachment[] {
  const list = (job.brief as { attachments?: unknown }).attachments;
  return Array.isArray(list) ? (list as AiJobAttachment[]) : [];
}

export async function uploadJobAttachments(jobId: string, files: File[]): Promise<AiJobAttachment[]> {
  if (files.length === 0) return [];
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not signed in');

  const payload = await Promise.all(
    files.map(async (file) => ({
      fileName: file.name,
      fileType: file.type || 'application/octet-stream',
      fileSize: file.size,
      fileData: await readAsBase64(file),
    }))
  );

  const res = await fetch('/.netlify/functions/r2-job-upload', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.access_token}`,
    },
    body: JSON.stringify({ jobId, files: payload }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `Upload failed (${res.status})`);

  return ((json.files as Array<{ fileName: string; publicUrl: string; fileType: string; fileSize: number }>) || []).map(
    (file) => ({
      name: file.fileName,
      url: file.publicUrl,
      type: file.fileType,
      size: file.fileSize,
    })
  );
}

export function formatAttachmentLines(attachments: AiJobAttachment[]): string[] {
  return attachments.map((file) => `- [${file.name}](${file.url})`);
}

/** Queue note plus a markdown list of links, so the agent gets the files from the note alone. */
export function appendAttachmentsToNote(note: string, attachments: AiJobAttachment[]): string {
  const base = note.trim();
  if (attachments.length === 0) return base;
  const block = ['Attachments:', ...formatAttachmentLines(attachments)].join('\n');
  return base ? `${base}\n\n${block}` : block;
}

/** Enqueue, then upload. Object keys need the job id, so the row goes in first.
    If the upload fails the job is cancelled so nothing half-formed waits for pickup. */
export async function enqueueAiJobWithFiles(input: EnqueueAiJobInput, files: File[]): Promise<AiJob> {
  const job = await enqueueAiJob(input);
  if (files.length === 0) return job;

  let attachments: AiJobAttachment[];
  try {
    attachments = await uploadJobAttachments(job.id, files);
  } catch (err) {
    await cancelAiJob(job.id).catch((cancelErr) => {
      console.error('Could not cancel job after a failed upload:', cancelErr);
    });
    const reason = err instanceof Error ? err.message : 'Upload failed';
    throw new Error(`Attachments failed to upload, so nothing was queued. ${reason}`);
  }

  const { data, error } = await supabase
    .from('ai_jobs')
    .update({
      brief: { ...job.brief, attachments },
      queue_note: appendAttachmentsToNote(job.queue_note, attachments),
    })
    .eq('id', job.id)
    .select('*')
    .single();
  if (error) throw error;
  return data as AiJob;
}
